"use client";
import React from "react";
import { motion } from "motion/react";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";
import Link from "next/link";
import { useTranslation } from "react-i18next";

export const BlogsPage: React.FC = () => {
  const { t } = useTranslation("common");
  const blogPosts = [
    {
      title: "2026 Packaging Trends: What to Expect",
      excerpt:
        "Explore the latest trends shaping the packaging industry, from sustainable materials to smart packaging technology.",
      category: "Industry Trends",
      date: "February 15, 2026",
      readTime: "5 min read",
      image:
        "https://images.unsplash.com/photo-1631010231888-777b6285ef84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjdXN0b20lMjByZXRhaWwlMjBwcm9kdWN0JTIwYm94ZXN8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "2026-packaging-trends",
    },
    {
      title: "The Ultimate Guide to Sustainable Packaging",
      excerpt:
        "Everything you need to know about eco-friendly packaging materials, certifications, and best practices.",
      category: "Sustainability",
      date: "February 10, 2026",
      readTime: "8 min read",
      image:
        "https://images.unsplash.com/photo-1755606396356-bdd7cd95df75?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGVjbyUyMHBhY2thZ2luZyUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "sustainable-packaging-guide",
    },
    {
      title: "How Custom Packaging Boosts E-commerce Sales",
      excerpt:
        "Discover how premium unboxing experiences can increase customer loyalty and drive repeat purchases.",
      category: "Case Studies",
      date: "February 5, 2026",
      readTime: "6 min read",
      image:
        "https://images.unsplash.com/photo-1631010231931-d2c396b444ec?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxlY29tbWVyY2UlMjBzaGlwcGluZyUyMGJveGVzJTIwZGVsaXZlcnl8ZW58MXx8fHwxNzcxNjA3OTQ2fDA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "ecommerce-packaging-sales",
    },
    {
      title: "Food Packaging Safety Standards Explained",
      excerpt:
        "A comprehensive overview of FDA regulations and safety requirements for food-grade packaging.",
      category: "Industry Insights",
      date: "January 28, 2026",
      readTime: "7 min read",
      image:
        "https://images.unsplash.com/photo-1766050588070-b4ec0134ff8d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwZ3JhZGUlMjBwYWNrYWdpbmclMjBjb250YWluZXJzfGVufDF8fHx8MTc3MTYwNzk0Nnww&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "food-packaging-safety",
    },
    {
      title: "Innovations in Corrugated Packaging",
      excerpt:
        "New technologies and designs making corrugated packaging stronger, lighter, and more sustainable.",
      category: "Packaging Innovation",
      date: "January 20, 2026",
      readTime: "5 min read",
      image:
        "https://images.unsplash.com/photo-1739204618173-3e89def7140f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3JydWdhdGVkJTIwY2FyZGJvYXJkJTIwYm94ZXMlMjB3YXJlaG91c2V8ZW58MXx8fHwxNzcxNjA3OTQ1fDA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "corrugated-packaging-innovations",
    },
    {
      title: "Success Story: Transforming Retail Packaging",
      excerpt:
        "How we helped a major retailer redesign their packaging strategy to increase shelf appeal and reduce costs.",
      category: "Case Studies",
      date: "January 15, 2026",
      readTime: "6 min read",
      image:
        "https://images.unsplash.com/photo-1635961179148-3d886568775c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFja2FnaW5nJTIwZmFjdG9yeSUyMG1vZGVybnxlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080",
      slug: "retail-packaging-transformation",
    },
  ];

  const categories = [
    "All",
    "Industry Trends",
    "Sustainability",
    "Case Studies",
    "Packaging Innovation",
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        {/* Glassmorphism Background with Gradient Orbs */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Animated gradient orbs */}
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-walnut-400/30 dark:bg-walnut-600/20 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-beige-400/25 dark:bg-beige-700/15 rounded-full blur-[80px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-grey-300/20 dark:bg-grey-700/10 rounded-full blur-[90px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block mb-6">
              <div className="backdrop-blur-xl bg-walnut-500/10 dark:bg-walnut-500/20 px-6 py-2 rounded-full border border-walnut-500/20 dark:border-walnut-400/30">
                <span className="text-walnut-700 dark:text-walnut-300 font-medium">
                  {t("blogPage.hero.badge")}
                </span>
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-walnut-900 dark:text-white mb-6">
              {t("blogPage.hero.title")}
            </h1>
            <p className="text-xl text-walnut-700 dark:text-walnut-300 max-w-3xl mx-auto">
              {t("blogPage.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant="glass"
                className="text-walnut-900 dark:text-white"
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <Link key={index} href={`/blog/${post.slug}`}>
                <GlassCard className="p-0 overflow-hidden group cursor-pointer hover:scale-[1.02] transition-all duration-300">
                  <div className="relative overflow-hidden">
                    <ImageWithFallback
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-lg backdrop-blur-xl bg-walnut-600/80 text-white text-sm">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-walnut-600 dark:text-walnut-400 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {post.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {post.readTime}
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-3 group-hover:text-walnut-700 dark:group-hover:text-walnut-300 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-walnut-700 dark:text-walnut-300 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-walnut-700 dark:text-walnut-300 group-hover:text-walnut-900 dark:group-hover:text-white transition-colors">
                      {t("marketingPages.blogs.readMore")}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </GlassCard>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <GlassCard className="p-12 text-center">
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("blogPage.newsletter.title")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-8">
              {t("blogPage.newsletter.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder={t("blogPage.newsletter.placeholder")}
                className="flex-1 px-6 py-3 rounded-lg backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500"
              />
              <Button variant="gradient">
                {t("blogPage.newsletter.button")}
              </Button>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
